import { schemas } from '@showrunner/scrapi'

import {
  isViewType,
  type SideBySideContextOption,
  type SnapshotViewType,
} from '#components/SnapshotLand'
import {
  renameSnapshots,
  snapshotQueryKey,
} from '#components/SnapshotLand/useSnapshotLandData'
import { getRootStore } from '#state/models/getRootStore'
import { MixpanelEventName } from '#util/mixpanel/eventNames'
import { SOME_DIFF_CONTEXT_LINES } from '#util/printing'
import { SnapshotSummary } from '#util/ScriptoApiClient/types'

import { BaseModel } from '../BaseModel'

// these are query param keys
const VIEW = 'view'
const SNAPSHOT1 = 'snap1'
const SNAPSHOT2 = 'snap2'

// We've gone back and forth over whether the left snapshot in a side-by-side
// view should be the newer or older-- so, the params we use and the meanings
// of the sides should not be assumed. The map is handled here
type SnapKey = typeof SNAPSHOT1 | typeof SNAPSHOT2
const SideParamMap: Record<LeftRight, SnapKey> = {
  left: SNAPSHOT2,
  right: SNAPSHOT1,
}

// in a URL search we use snap1 and snap2 as keys and the values
// look like <snapshot-id> OR <snapshot-id>_<block-id>
type SnapParam = {
  snapshotId: string
  blockId?: string
}
const parseSnapQueryParam = (
  rawValue: string | undefined,
): SnapParam | undefined => {
  if (rawValue) {
    const [snapshotId, blockId] = rawValue.split('_')
    const isValidSnapshot =
      snapshotId === 'current' || schemas.Uuid.safeParse(snapshotId).success

    if (isValidSnapshot) {
      const result: SnapParam = { snapshotId }
      if (schemas.Uuid.safeParse(blockId).success) {
        result.blockId = blockId
      }
      return result
    }
  }
}

// This handles state for the script history overlay. However,
// we do not store the actual snapshot data in MST, that's all
// done in tanstack. This is just for managing the view state,
// query params, etc.
export const SnapshotLandState = BaseModel.named('SnapshotLand')
  .props({
    monochrome: false,
  })
  .views((self) => ({
    get currentView(): SnapshotViewType {
      const param = getRootStore(self).location.getQueryParam(VIEW)
      if (isViewType(param)) {
        return param
      }
      return 'static'
    },
    // the raw value xxx of `?snap1=xxxxx`
    get rawSnap1(): string | undefined {
      return getRootStore(self).location.getQueryParam(SNAPSHOT1)
    },
    get rawSnap2(): string | undefined {
      return getRootStore(self).location.getQueryParam(SNAPSHOT2)
    },
    // parse the raw query param value into a snapId & possibly a blockId
    get snapAndBlock1(): SnapParam | undefined {
      return parseSnapQueryParam(this.rawSnap1)
    },
    get snapAndBlock2(): SnapParam | undefined {
      return parseSnapQueryParam(this.rawSnap2)
    },
    // just the ID from the query param
    get snap1(): string | undefined {
      return this.snapAndBlock1?.snapshotId
    },
    get snap2(): string | undefined {
      return this.snapAndBlock2?.snapshotId
    },
    get filter(): 'all' | 'manual' {
      return getRootStore(self).user.prefs.snapshotFilter ?? 'all'
    },
    get pageSize(): number {
      if (getRootStore(self).view.isDebugEnabled('small-page')) {
        return 5
      }
      return 50
    },
    get sideBySideContextLines(): number | null {
      const pref: SideBySideContextOption =
        getRootStore(self).user.prefs.snapshotLinesOfContext ?? 'some'
      return pref === 'none'
        ? 0
        : pref === 'all'
          ? null
          : SOME_DIFF_CONTEXT_LINES
    },
  }))
  .actions((self) => ({
    setFilter(snapshotFilter: 'all' | 'manual') {
      if (self.filter !== snapshotFilter) {
        getRootStore(self).user.updatePreferences({ snapshotFilter })
      }
    },
    selectSlene(sleneId: string, side: LeftRight) {
      const key = SideParamMap[side]
      const snapId = key === 'snap1' ? self.snap1 : self.snap2
      if (snapId) {
        getRootStore(self).location.updateQueryParams({
          [key]: `${snapId}_${sleneId}`,
        })
      }
    },
    deselectSlene(side: LeftRight) {
      const key = SideParamMap[side]
      const snapId = key === 'snap1' ? self.snap1 : self.snap2
      if (snapId) {
        getRootStore(self).location.updateQueryParams({
          [key]: snapId,
        })
      }
    },
    selectView(view: string) {
      if (isViewType(view)) {
        getRootStore(self).location.updateQueryParams({ view })
        getRootStore(self).user.updatePreferences({ snapshotTab: view })
      }
    },
    selectSnapshotUrl(id: string): string {
      const { currentView, snap1, snap2 } = self
      // if the view is static, then we swap out snapshot1,
      // otherwise, we look for an empty slot and if none exist
      // we do nothing
      const currentUrl = new URL(window.location.href)
      if (currentView === 'static' || !snap1) {
        currentUrl.searchParams.set(SNAPSHOT1, id)
      } else if (!snap2) {
        currentUrl.searchParams.set(SNAPSHOT2, id)
      }
      return currentUrl.toString()
    },
    dismissSnapshot(position: 1 | 2) {
      const key = position === 1 ? SNAPSHOT1 : SNAPSHOT2
      getRootStore(self).location.updateQueryParams({ [key]: null })
    },
    dismissTop() {
      this.dismissSnapshot(2)
    },
    dismissBottom() {
      // we shuffle the remaining snapshot into slot 1 when present to
      // ensure it will be visible if/when folks navigate away from a comparison route
      const snap1 = self.rawSnap2 ?? null
      getRootStore(self).location.updateQueryParams({ [SNAPSHOT1]: snap1 })
      this.dismissSnapshot(2)
    },
    swapSnapshots() {
      const { rawSnap1, rawSnap2 } = self
      if (rawSnap1 && rawSnap2) {
        getRootStore(self).location.updateQueryParams({
          [SNAPSHOT1]: rawSnap2,
          [SNAPSHOT2]: rawSnap1,
        })
      }
    },
    setSideBySideContextLines(value: SideBySideContextOption) {
      getRootStore(self).user.updatePreferences({
        snapshotLinesOfContext: value,
      })
    },
    setMonochrome(value: boolean) {
      self.monochrome = value
    },

    setRevisionMarkPlacement(value: 'left' | 'right') {
      getRootStore(self).user.updatePreferences({
        snapshotRevisionMarkPlacement: value,
      })
    },

    setHightlightAdditions(value: boolean) {
      getRootStore(self).user.updatePreferences({
        snapshotHighlightAdditions: value,
      })
    },

    setShowDeletions(value: boolean) {
      getRootStore(self).user.updatePreferences({
        snapshotShowDeletions: value,
      })
    },

    updateCachedSnapName({
      scriptId,
      snapshotId,
      name,
    }: {
      scriptId: string
      snapshotId: string
      name: string
    }) {
      // if the snapshot is in the tanstack cache, we want to update it there
      // as well
      const queryKey = snapshotQueryKey({ scriptId, snapshotId })
      const cachedSnapshot =
        self.environment.queryClient.getQueryData<SnapshotSummary>(queryKey)
      if (cachedSnapshot) {
        self.environment.queryClient.setQueryData(queryKey, {
          ...cachedSnapshot,
          name,
        })
      }
    },

    async renameSnapshot(opts: {
      scriptId: string
      snapshotId: string
      name: string
    }) {
      await getRootStore(self).doDebug()
      await renameSnapshots(getRootStore(self).scrapi, opts)
      this.updateCachedSnapName(opts)
    },

    trackSnapshotEvent(
      eventName: MixpanelEventName,
      context?: { [key: string]: unknown },
    ) {
      const { currentView } = self
      self.trackEvent(eventName, {
        topic: 'snapshot-land',
        currentView,
        ...context,
      })
    },
  }))
